import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { BaseState, withCrudOperations } from '../crud.state';
import { DataLayer } from '../../models/data-layer';
import { withDataLayerSelectors } from './data-layer.selectors';
import { DataLayerService } from '../../services/data-layer/data-layer.service';
import { inject } from '@angular/core';
import { ActiveUserStore } from '../user/active-user.state';
import { withLogging } from '../features/logger.feature';
import { makeUniqueNumberArray } from '../../utility/array.utility';

export type DataLayerState = BaseState<DataLayer>;

export const initialState: DataLayerState = {
  items: {},
  loading: false,
  selectedId: '',
  selection: [],
  initialized: false,
  itemsLoaded: false,
  version: 0,
  lastPersistedId: '',
  entityType: 'DataLayer',
  entityLabel: 'Jahr',
  filter: {
    query: '',
    category: [],
    tags: undefined,
    products: undefined,
    published: null,
    locked: null,
    ids: undefined,
  },
  events: {
    add: ['isSelectable'],
    update: ['runBookmarkValidation'],
  },
  trigger: undefined,
};

export const DataLayerStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCrudOperations<DataLayer>(DataLayerService),
  withLogging(initialState.entityType),
  withDataLayerSelectors(),
  withMethods((store, dataLayerService = inject(DataLayerService)) => {
    const methods = {
      filterByParent(parentId: string, hasGeoData = false): DataLayer[] {
        return Object.values(store.items())
          .filter((item: DataLayer) => {
            if (hasGeoData) {
              return (
                item.parent === parentId &&
                item.geoData !== '' &&
                item.geoData !== undefined &&
                item.geoData !== null
              );
            } else {
              return item.parent === parentId;
            }
          })
          .sort((a, b) => b.year - a.year) as DataLayer[];
      },
      filterByParentAndYear(parentId: string, year: number): DataLayer {
        return Object.values(store.items()).filter((item: DataLayer) => {
          return item.parent === parentId && Number(item.year) === year;
        })[0];
      },
      filterByParentAndYears(parentId: string, years: number[]): DataLayer[] {
        return Object.values(store.items()).filter((item: DataLayer) => {
          return item.parent === parentId && years.includes(item.year);
        });
      },
      getYearsByParent(parentId: string, desc = false) {
        return Object.values(store.items())
          .filter((item: DataLayer) => {
            return item.parent === parentId;
          })
          .map((item: DataLayer) => {
            return +item.year;
          })
          .sort((a, b) => (desc ? b - a : a - b)) as number[];
      },
      getYearsByParents(parentIds: string[]) {
        return makeUniqueNumberArray(
          Object.values(store.items())
            .filter((item: DataLayer) => {
              return parentIds.includes(item.parent);
            })
            .map((item: DataLayer) => {
              return +item.year;
            })
            .sort((a, b) => a - b)
        ) as number[];
      },
      getAvailableTypeIds(): string[] {
        return Object.keys(store.groupedByType());
      },
      /**
       * Checks if the specified parent has the given year in its list of years.
       *
       * @param {string} parentId - The ID of the parent to check.
       * @param {number} year - The year to verify against the parent's list of years.
       * @return {boolean} True if the parent has the specified year, otherwise false.
       */
      parentHasYear(parentId: string, year: number): boolean {
        return this.getYearsByParent(parentId).includes(year);
      },
      /**
       * Retrieves the latest data entry associated with the given parent identifier.
       *
       * @param {string} parentId - The identifier of the parent item used to filter data.
       * @return {DataLayer} The latest data entry corresponding to the specified parent identifier.
       */
      getLatestByParent(parentId: string, idxModifier = 0): DataLayer {
        return methods.filterByParent(parentId)[0 + idxModifier]; // Use the method from the variable
      },
      /**
       * Retrieves the latest non-forecast data entry associated with the given parent identifier.
       *
       * @param {string} parentId - The identifier of the parent item used to filter data.
       * @return {DataLayer|undefined} The latest non-forecast data entry, or undefined if none exists.
       */
      getLatestNonForecastByParent(parentId: string): DataLayer | undefined {
        return methods.filterByParent(parentId).find(layer => !layer.isForecast);
      },
      /**
       * Resolves the default data layer to load for a given data layer type, e.g. for the Atlas.
       * Tries, in order: the type's configured `defaultLayer` year (if it exists and has data),
       * then the latest non-forecast year, then the latest year overall.
       *
       * @param {{_id: string; defaultLayer?: number}} dataLayerType - The data layer type to resolve a default layer for.
       * @param {(dataLayerId: string) => boolean} hasData - Predicate checking whether a data layer actually contains data.
       * @return {DataLayer|undefined} The resolved default data layer.
       */
      resolveDefaultByType(
        dataLayerType: { _id: string; defaultLayer?: number },
        hasData: (dataLayerId: string) => boolean
      ): DataLayer | undefined {
        if (dataLayerType.defaultLayer) {
          const byDefault = methods.filterByParentAndYear(
            dataLayerType._id,
            dataLayerType.defaultLayer
          );
          if (byDefault && hasData(byDefault._id)) return byDefault;
        }
        return (
          methods.getLatestNonForecastByParent(dataLayerType._id) ??
          methods.getLatestByParent(dataLayerType._id)
        );
      },
      /**
       * Retrieves the latest GeoData ID for the specified parent ID.
       *
       * @param {string} parentId - The ID of the parent entity for which the GeoData ID is to be retrieved.
       * @param {number} [idxModifier=0] - Optional index modifier to adjust the result index.
       * @return {string} The GeoData ID corresponding to the specified parent entity.
       */
      getLatestGeoDataIdByParent(parentId: string, idxModifier = 0): string {
        const layer = methods.filterByParent(parentId, true)[0 + idxModifier]; // Use the method from the variable
        return <string>layer?.geoData || '';
      },
      /**
       * Retrieves a list of IDs from layers that match the specified years under a given parent type.
       *
       * @param {string} parent - The key identifying the parent type to filter layers from.
       * @param {number[]} years - An array of years to filter the layers by.
       * @return {string[]} An array of IDs corresponding to layers that match the given years.
       */
      getIdsByYears(parent: string, years: number[]): string[] {
        return (
          store
            .groupedByType()
            [parent]?.filter(layer => years.includes(Number(layer.year)))
            .map(layer => layer._id) || []
        );
      },
      /**
       * Retrieves the geoData ID from sibling layers based on the given parent ID.
       *
       * @param {string} parentId - The ID of the parent layer used to filter sibling layers.
       * @return {string} The geoData ID of the first sibling layer that contains geoData, or an empty string if none is found.
       */
      getGeoDataIdFromSiblings(parentId: string): string {
        const parentLayers = this.filterByParent(parentId);
        if (parentLayers.length) {
          for (const layer of parentLayers) {
            if (layer.geoData) return layer.geoData;
          }
          return '';
        }
        return '';
      },
      /**
       * Retrieves the geoData ID from sibling layers based on the given parent ID.
       *
       * @param {string} parentId - The ID of the parent layer used to filter sibling layers.
       * @return {string} The geoData ID of the first sibling layer that contains geoData, or an empty string if none is found.
       */
      getGeoDataIdsFromSiblings(parentId: string): string[] {
        const parentLayers = this.filterByParent(parentId);
        const geoDataIds: string[] = [];
        if (parentLayers.length) {
          for (const layer of parentLayers) {
            if (!layer.geoData) continue;
            geoDataIds.push(layer.geoData);
          }
          return geoDataIds;
        }
        return [];
      },
      getSortedByIds(ids: string[]) {
        return ids
          .map(id => store.items()[id])
          .filter(layer => layer !== undefined)
          .sort((a, b) => b.year - a.year) as DataLayer[];
      },
      getGeoDataIdById(id: string): string {
        const layer = store.items()[id];
        return layer?.geoData || '';
      },
      /**
       * Fetches and updates the state with items based on the provided parent IDs and type-year mappings.
       *
       * @param {string[]} parentIds - An array of parent ID strings used to filter the items.
       * @param {Record<string, string[]>} typeYears - A record where each key is a type and the value is an array of years.
       * @return {Promise<void>} A promise that resolves once the operation is completed and the state is updated.
       */
      async findByParentIds(
        parentIds: string[],
        typeYears: Record<string, string[]>
      ) {
        patchState(store, { loading: true });

        const items = await dataLayerService.getByParentIds(
          parentIds,
          typeYears
        );
        patchState(store, {
          items: items,
          loading: false,
          initialized: true,
          itemsLoaded: true,
        });
      },
      getSiblings(layer: DataLayer) {
        return this.filterByParent(layer.parent);
      },
      getLayersByGeoData(geoDataId: string) {
        const all = store.getAllDataLayers();
        return all
          .filter((layer: DataLayer) => layer.geoData === geoDataId)
          .sort(
            (a: DataLayer, b: DataLayer) => Number(b.year) - Number(a.year)
          ) as DataLayer[];
      },
      getParentIdByGeoDataId(geoDataId: string): string | null {
        const layer = this.getLayersByGeoData(geoDataId).at(0);
        if (layer) {
          return layer.parent;
        } else {
          return null;
        }
      },
    };

    // Return the methods variable
    return methods;
  }),
  withHooks({
    onInit(store, activeUserStore = inject(ActiveUserStore)) {
      if (
        activeUserStore.canSeeAllInWorkspaceType('dataLayerType') &&
        activeUserStore.context() === 'dataBackend'
      ) {
        store.loadAllItemsByPromise();
      }
    },
    onDestroy() {
      console.log('on destroy');
    },
  })
);
